import {
  useStartLoginMutation,
  useSubmitCodeMutation,
  useWhatsappAccountsQuery,
  type WhatsappLoginMethod,
  type WhatsappLoginStarted,
} from '@queries/whatsapp/accounts';
import QRCode from 'qrcode';
import { createEffect, createSignal, onMount, Show } from 'solid-js';

/** The login methods offered in the picker. QR + PAIRING link this phone as a
 * companion device (web transport); SMS/VOICE/WHATSAPP register an independent
 * number (mobile transport) and deliver a one-time code. */
const METHODS: { value: WhatsappLoginMethod; label: string }[] = [
  { value: 'QR', label: 'QR code — scan with your phone' },
  { value: 'PAIRING', label: 'Pairing code — enter on your phone' },
  { value: 'SMS', label: 'SMS code — register a new number' },
  { value: 'VOICE', label: 'Voice call code — register a new number' },
  { value: 'WHATSAPP', label: 'WhatsApp code — register a new number' },
];

/**
 * Links a new WhatsApp account. Supports both transports the gateway exposes:
 * QR / pairing-code companion linking (web) and SMS/voice/WhatsApp OTP number
 * registration (mobile). Defaults to a QR login started on mount; other methods
 * take a phone number and are started from the button. Closes itself once the
 * account reports `CONNECTED` (polled via {@link useWhatsappAccountsQuery}).
 */
export function AddAccountModal(props: { onClose: () => void }) {
  const startLogin = useStartLoginMutation();
  const submitCode = useSubmitCodeMutation();
  const accounts = useWhatsappAccountsQuery(3000);

  const [method, setMethod] = createSignal<WhatsappLoginMethod>('QR');
  const [phone, setPhone] = createSignal('');
  const [started, setStarted] = createSignal<WhatsappLoginStarted | undefined>();
  const [qrDataUrl, setQrDataUrl] = createSignal<string | undefined>();
  const [error, setError] = createSignal<string | undefined>();
  const [code, setCode] = createSignal('');

  const needsPhone = () => method() !== 'QR';
  const canStart = () =>
    !startLogin.isPending && (!needsPhone() || phone().trim().length > 0);

  async function begin() {
    const m = method();
    const phoneE164 = needsPhone() ? phone().trim() : undefined;
    setError(undefined);
    setQrDataUrl(undefined);
    setStarted(undefined);
    try {
      const result = await startLogin.mutateAsync({ method: m, phoneE164 });
      setStarted(result);
      if (result.error) setError(result.error);
      if (result.qrPayload) {
        setQrDataUrl(
          await QRCode.toDataURL(result.qrPayload, { width: 264, margin: 1 })
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // QR is the common case — start it immediately so the code is ready to scan.
  onMount(() => void begin());

  // Switching method clears any stale QR/pairing/error so the picker is honest
  // about what will happen when the user presses Start.
  function chooseMethod(m: WhatsappLoginMethod) {
    if (m === method()) return;
    setMethod(m);
    setStarted(undefined);
    setQrDataUrl(undefined);
    setError(undefined);
    setCode('');
    if (m === 'QR') void begin();
  }

  // Close once the linking account reports CONNECTED (or the gateway already
  // returned a terminal `linked`).
  createEffect(() => {
    const s = started();
    if (!s) return;
    if (s.linked) {
      props.onClose();
      return;
    }
    const account = accounts.data?.find((a) => a.accountId === s.accountId);
    if (account?.state === 'CONNECTED') props.onClose();
  });

  const startLabel = () => {
    if (startLogin.isPending) return 'Working…';
    switch (method()) {
      case 'QR':
        return 'Regenerate QR';
      case 'PAIRING':
        return 'Get pairing code';
      default:
        return 'Send code';
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={props.onClose}
    >
      <div
        class="w-[360px] rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">Link a WhatsApp account</h2>
          <button
            class="text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
            onClick={props.onClose}
          >
            ✕
          </button>
        </div>

        <label class="mb-1 block text-xs font-medium text-neutral-500">
          Login method
        </label>
        <select
          class="mb-3 w-full rounded border border-neutral-300 bg-transparent px-2 py-1.5 text-sm dark:border-neutral-700"
          value={method()}
          onChange={(e) =>
            chooseMethod(e.currentTarget.value as WhatsappLoginMethod)
          }
        >
          {METHODS.map((m) => (
            <option value={m.value}>{m.label}</option>
          ))}
        </select>

        <Show when={needsPhone()}>
          <label class="mb-1 block text-xs font-medium text-neutral-500">
            Phone number (international, e.g. +8613800138000)
          </label>
          <input
            class="mb-3 w-full rounded border border-neutral-300 bg-transparent px-2 py-1.5 text-sm dark:border-neutral-700"
            placeholder="+8613800138000"
            inputmode="tel"
            value={phone()}
            onInput={(e) => setPhone(e.currentTarget.value)}
          />
        </Show>

        <Show
          when={!error()}
          fallback={<p class="text-sm text-red-500">{error()}</p>}
        >
          {/* QR image (web/QR method). */}
          <Show when={method() === 'QR' && started()?.qrPayload}>
            <div class="flex flex-col items-center gap-3">
              <Show
                when={qrDataUrl()}
                fallback={
                  <div class="h-[264px] w-[264px] animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
                }
              >
                <img
                  src={qrDataUrl()}
                  alt="WhatsApp QR code"
                  class="rounded"
                  width={264}
                  height={264}
                />
              </Show>
              <p class="text-center text-sm text-neutral-500">
                On your phone: WhatsApp → Settings → Linked devices → Link a
                device, then scan this code.
              </p>
            </div>
          </Show>

          {/* Pairing code (web/pairing method): typed into WhatsApp. */}
          <Show when={started()?.pairingCode}>
            <div class="flex flex-col items-center gap-3">
              <div class="rounded-lg bg-neutral-100 px-4 py-3 font-mono text-2xl tracking-[0.3em] dark:bg-neutral-800">
                {started()?.pairingCode}
              </div>
              <p class="text-center text-sm text-neutral-500">
                On your phone: WhatsApp → Linked devices → Link with phone number
                instead, then enter this code.
              </p>
            </div>
          </Show>

          {/* One-time code entry (mobile SMS/voice/WhatsApp registration). */}
          <Show when={started()?.codeRequested}>
            <p class="mb-2 text-sm text-neutral-500">
              Enter the code sent to {phone().trim() || 'your number'}.
            </p>
            <div class="flex gap-2">
              <input
                class="flex-1 rounded border border-neutral-300 bg-transparent px-2 py-1 text-sm dark:border-neutral-700"
                placeholder="Verification code"
                inputmode="numeric"
                value={code()}
                onInput={(e) => setCode(e.currentTarget.value)}
              />
              <button
                class="rounded bg-green-600 px-3 py-1 text-sm text-white disabled:opacity-50"
                disabled={submitCode.isPending || !code().trim()}
                onClick={() => {
                  const s = started();
                  if (s) {
                    void submitCode.mutateAsync({
                      accountId: s.accountId,
                      code: code(),
                    });
                  }
                }}
              >
                Submit
              </button>
            </div>
          </Show>

          {/* Idle hint before a non-QR flow is started. */}
          <Show when={needsPhone() && !started() && !startLogin.isPending}>
            <p class="text-sm text-neutral-500">
              Enter the phone number, then press “{startLabel()}”.
            </p>
          </Show>
          <Show when={startLogin.isPending && !started()}>
            <p class="text-sm text-neutral-500">Starting login…</p>
          </Show>
        </Show>

        <button
          class="mt-4 w-full rounded border border-neutral-300 py-1.5 text-sm hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
          disabled={!canStart()}
          onClick={() => void begin()}
        >
          {startLabel()}
        </button>
      </div>
    </div>
  );
}
